/**
 * DrillCard — "Today's Drill" dashboard widget.
 *
 * EPIC-08-12: Shows status (available/completed/locked) and a Start button.
 * Consumes GET /api/v1/drills/today.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import styles from './DrillCard.module.css';

interface DrillQuestion {
  question: string;
  type: string;
  options: string[] | null;
  correct_answer: string;
  explanation: string;
}

interface DrillState {
  status: 'available' | 'completed' | 'locked';
  drill_date: string;
  questions: DrillQuestion[] | null;
  weak_areas: { topic: string; track: string }[] | null;
}

export function DrillCard(): JSX.Element {
  const { t } = useTranslation('training');
  const navigate = useNavigate();
  const [drill, setDrill] = useState<DrillState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<{ data: DrillState }>('/drills/today')
      .then((res) => setDrill(res.data.data))
      .catch(() => setDrill(null))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading || drill === null) return <></>;

  if (drill.status === 'locked') {
    return (
      <div className={`${styles.card} ${styles.locked}`}>
        <h3 className={styles.title}>{t('drill.title')}</h3>
        <p className={styles.lockedMsg}>{t('drill.lockedMessage')}</p>
      </div>
    );
  }

  if (drill.status === 'completed') {
    return (
      <div className={`${styles.card} ${styles.completed}`}>
        <h3 className={styles.title}>{t('drill.title')}</h3>
        <p className={styles.completedMsg}>{t('drill.completedMessage')}</p>
      </div>
    );
  }

  const questionCount = drill.questions?.length ?? 0;
  const topics = drill.weak_areas?.map((wa) => wa.topic).join(', ') ?? '';

  return (
    <div className={styles.card} data-testid="drill-card-available">
      <h3 className={styles.title}>{t('drill.title')}</h3>
      <p className={styles.meta}>
        {t('drill.estimatedTime')} · {t('drill.questionCount', { count: questionCount })}
      </p>
      {topics && <p className={styles.topics}>{t('drill.topics')}: {topics}</p>}
      <button
        className={styles.startBtn}
        onClick={() => navigate('/drill/today')}
      >
        {t('drill.startButton')}
      </button>
    </div>
  );
}
